// Why a terminology lookup came back empty, on the client side.
// (TGCLOUD-1785)
//
// ── THE DEFECT THIS CLOSES ────────────────────────────────────────────────
// Every terminology consumer in the app keyed its "unavailable" warning off
// the `.catch` of its request. But the backend proxy answers HTTP 200 with an
// empty list for an unreachable service, for a code system an admin has
// switched off, and for a vocabulary holding no rows — so `.catch` never fired
// for any real fault. GenotypesMenu was worse than silent: its success path
// ran `if (isError) setIsError(false)`, so an empty 200 actively CLEARED the
// warning.
//
// On 2026-09-04 a deploy left the HGNC vocabulary with zero genes. For five
// days a clinician typing "BRCA1" into Pedigree, Genotypes saw an empty
// dropdown reading "No options" — indistinguishable from "no gene by that
// name exists" — and no banner anywhere in the app.
//
// The backend now names the cause on the response (`available: false` plus a
// `reason`). This module is the single place that reads it, so the nine
// affected consumers share one rule instead of nine heuristics.
//
// ── COPY LIVES HERE, NOT ON THE WIRE ─────────────────────────────────────
// The server sends a `message` too, useful for logs and for API consumers,
// but the strings shown to a clinician are defined here so they stay
// translatable through useAutoTranslation() like the rest of the UI.

// The backend's REASON (backend/services/terminologyAvailability.js), value for
// value; terminologyAvailability.test.js binds the two.
export const TERMINOLOGY_REASON = Object.freeze({
  UNREACHABLE: 'service-unreachable',
  DISABLED: 'system-disabled',
  EMPTY: 'vocabulary-empty',
  // Longer than the terminology service searches (TGCLOUD-1890 QA). The
  // service is up: this must never read as an outage.
  TOO_LONG: 'query-too-long'
});

// Kept as the existing wording for the unreachable case, because that string
// is already translated and already asserted by the characterisation tests.
const REASON_COPY = Object.freeze({
  [TERMINOLOGY_REASON.UNREACHABLE]:
    'The terminology service is unreachable. Please contact admin.',
  [TERMINOLOGY_REASON.DISABLED]:
    'This code system has been switched off in Settings, so no options can be suggested.',
  [TERMINOLOGY_REASON.EMPTY]:
    'This code system holds no reference data, so no options can be suggested. Please contact admin.',
  [TERMINOLOGY_REASON.TOO_LONG]:
    'This search is too long to run in full. Use 10 words or fewer, and no more than 200 characters.'
});

const FALLBACK_COPY = REASON_COPY[TERMINOLOGY_REASON.UNREACHABLE];

/**
 * Read availability off a successful response body.
 *
 * @param {object} data a terminology response body
 * @returns {{reason: string, message: string, sources?: string[]}|null}
 *   null when the lookup DID run, which includes a genuine no-match. A
 *   no-match must keep reading as a no-match.
 */
export function readTerminologyAvailability(data) {
  if (!data || typeof data !== 'object') return null;
  // Only an explicit false is a refusal. A body with no `available` field at
  // all is an older/other endpoint answering normally, not a fault — inferring
  // one from absence is how a false outage warning gets in front of a
  // clinician.
  if (data.available !== false) return null;

  const reason = data.reason || TERMINOLOGY_REASON.UNREACHABLE;
  return {
    reason,
    message: REASON_COPY[reason] || FALLBACK_COPY,
    ...(Array.isArray(data.unavailableSources) && data.unavailableSources.length
      ? { sources: data.unavailableSources }
      : {})
  };
}

/**
 * The same shape for a request that actually threw. Still reachable: a 5xx
 * from our own Express layer, a dropped connection, an auth failure.
 */
export function terminologyAvailabilityFromError() {
  return {
    reason: TERMINOLOGY_REASON.UNREACHABLE,
    message: FALLBACK_COPY
  };
}

/**
 * Empty-state text for an Autocomplete.
 *
 * MUI's default is "No options", which is a claim about the vocabulary. When
 * the lookup did not run, the field must not make that claim.
 *
 * @param {object|null} availability from readTerminologyAvailability
 * @param {string}      typing       what to say below the minimum query length
 * @param {string}      noMatch      what to say when the lookup genuinely
 *                                   found nothing
 */
export function terminologyNoOptionsText(
  availability,
  typing = 'Type to search…',
  noMatch = 'No matches'
) {
  if (availability?.reason === TERMINOLOGY_REASON.TOO_LONG) {
    return 'Too long to search: use 10 words or fewer';
  }
  if (availability) return 'Lookup unavailable — this is not a result';
  return { typing, noMatch };
}
